// Resume-on-reload persistence (2c). The session handle is held in memory only
// (a deliberate security posture); to survive a reload we persist a resume
// record in sessionStorage — which survives a reload but dies on tab close and
// is not shared across tabs, bounding the token's exposure to this one tab.
//
// This module owns ONLY the storage + scoping decision ("is the stored session
// actually the one this launch should resume?"). The resume FLOW (rehydrate ->
// GET /step -> running/ended/clear) lives in App, which calls load/clear.
//
// Scoping rule (the safety core): only resume when the stored record matches the
// freshly-parsed launch context — same experiment, same identity source, and for
// platform participants the same external_id (so reusing one browser for a
// different Prolific submission never resumes the wrong person). Anonymous has no
// external param to compare, so it matches on experiment + source only. Sandbox
// is never persisted or resumed.

const KEY = "jolive.resume.v1";

export interface ResumeIdentity {
  source: string; // prolific | mturk | sona | anonymous | sandbox
  externalId: string;
}
export interface ResumeHandle {
  sessionId: string;
  sessionToken: string;
  idleTimeoutSeconds?: number;
}
interface ResumeRecord {
  experimentId: string;
  source: string;
  externalId: string;
  handle: ResumeHandle;
  savedAt: number;
}

export function saveResume(
  experimentId: string,
  identity: ResumeIdentity,
  handle: ResumeHandle,
): void {
  if (identity.source === "sandbox") return; // never persist a preview
  try {
    const rec: ResumeRecord = {
      experimentId,
      source: identity.source,
      externalId: identity.externalId,
      handle,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(KEY, JSON.stringify(rec));
  } catch {
    /* storage unavailable / quota — resume is best-effort, never fatal */
  }
}

export function clearResume(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

// Returns the stored handle ONLY if it is safe to resume for this launch.
// On any mismatch / corruption, clears the record and returns null so the
// caller falls back to a fresh session.
export function loadResume(
  experimentId: string,
  identity: ResumeIdentity,
): ResumeHandle | null {
  let rec: ResumeRecord | null = null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    rec = JSON.parse(raw) as ResumeRecord;
  } catch {
    clearResume();
    return null;
  }
  if (!rec || typeof rec !== "object") {
    clearResume();
    return null;
  }
  // Never resume a sandbox record (shouldn't exist; defensive).
  if (rec.source === "sandbox") {
    clearResume();
    return null;
  }
  // Different study open in this tab -> not ours.
  if (rec.experimentId !== experimentId) {
    clearResume();
    return null;
  }
  // Different platform source -> not ours.
  if (rec.source !== identity.source) {
    clearResume();
    return null;
  }
  // Platform participants: the external id must match (same browser reused for a
  // different submission must NOT resume the old one). Anonymous skips this.
  if (identity.source !== "anonymous" && rec.externalId !== identity.externalId) {
    clearResume();
    return null;
  }
  // Validate the stored handle shape.
  if (!rec.handle || !rec.handle.sessionId || !rec.handle.sessionToken) {
    clearResume();
    return null;
  }
  return rec.handle;
}
